import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDataLabel } from '@/lib/dataI18n';
import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { AssignmentsTraceCard } from '@/components/client/AssignmentsTraceCard';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import {
  Map as MapIcon, TrendingUp, AlertTriangle, ShieldAlert, Gauge, Info,
  Radar as RadarIcon, Download, Loader2, Crown, Target, Users, Sparkles, ArrowRight, Plus,
} from 'lucide-react';
import { ClientEvaluationRequestDialog } from '@/components/client/ClientEvaluationRequestDialog';
import { ClientSelfServeAssignDialog } from '@/components/client/ClientSelfServeAssignDialog';
import { clientApi } from '@/lib/adminApi';
import { DonutCard, BarsCard, SEM } from '@/components/client/SuiteAnalytics';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import TalentMapDetailSheet from '@/components/client/TalentMapDetailSheet';

// Founder freeze decision 2026-08-29: Succession tab hidden until a real
// succession engine (data contract + calculations + tests + founder acceptance) exists.
const SUCCESSION_TAB_ENABLED = false;

/**
 * Sprint A4 — TalentMap real workforce mapping.
 *
 * Data sources (zero new endpoints):
 *   • GET /client/intelligence/overview     → 9-box, role fit, value scores, KPIs, dept rollup
 *   • GET /client/talent-competency/gap-analysis → per-competency aggregates for the radar
 *
 * No seeded math. Every metric derived from the two payloads above. When
 * data is missing (e.g. no competency assessments), the empty-state branch
 * is shown — we never fabricate values.
 */

const QUADRANT_KEYS = [
  ['top_left', 'top_center', 'top_right'],
  ['mid_left', 'mid_center', 'mid_right'],
  ['low_left', 'low_center', 'low_right'],
];

const QUADRANT_TONE = {
  top_right:   'bg-success/15 text-success border-success/30',
  top_center:  'bg-success/10 text-success border-success/20',
  mid_right:   'bg-info/10 text-info border-info/30',
  top_left:    'bg-warning/15 text-warning border-warning/30',
  mid_center:  'bg-muted text-foreground border-border',
  low_right:   'bg-warning/10 text-warning border-warning/20',
  mid_left:    'bg-destructive/10 text-destructive border-destructive/20',
  low_center:  'bg-destructive/15 text-destructive border-destructive/30',
  low_left:    'bg-destructive/20 text-destructive border-destructive/40',
};

function KpiCard({ icon: Icon, label, value, hint, tone = 'primary', testid, onClick }) {
  const tones = {
    primary: 'from-primary/15 to-primary/5 text-primary',
    success: 'from-success/15 to-success/5 text-success',
    warning: 'from-warning/15 to-warning/5 text-warning',
    destructive: 'from-destructive/15 to-destructive/5 text-destructive',
    info: 'from-info/15 to-info/5 text-info',
  };
  const interactive = !!onClick;
  return (
    <Card
      className={`p-4 shadow-card transition-smooth ${interactive ? 'hover:shadow-elevated cursor-pointer' : ''}`}
      data-testid={testid}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium">{label}</p>
          <p className="text-2xl font-bold tabular-nums mt-0.5">{value}</p>
          {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
        </div>
      </div>
    </Card>
  );
}

export default function ClientTalentMap() {
  const { t } = useTranslation();
  const tData = useDataLabel();  // Session N-B — locale-aware dept name renderer
  const [overview, setOverview] = useState(null);
  const [gap, setGap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deptFilter, setDeptFilter] = useState('__all__');
  const [sheetState, setSheetState] = useState({ open: false, kind: null, payload: null });
  const [requestOpen, setRequestOpen] = useState(false);
  const [launchOpen, setLaunchOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, ga] = await Promise.all([
        clientApi.intelligenceOverview().catch(() => null),
        clientApi.talentCompetencyGapAnalysis().catch(() => null),
      ]);
      setOverview(ov);
      setGap(ga);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Derived data ---------------------------------------------------------
  const employees = overview?.employees || [];
  // Founder UAT — the department roll-up must stay consistent with the rest
  // of the TalentMap page: no released TalentMap reports → no analytics.
  const hasTalentMapData = employees.some((e) => e.suite_scores?.talentmap != null);
  const departments = useMemo(() => {
    // Session N-B — preserve `name_es` so the dropdown / chips render in
    // the active locale via tData below.
    const map = new Map();
    for (const e of employees) {
      if (e.department?.id) map.set(e.department.id, { name: e.department.name, name_es: e.department.name_es });
    }
    return Array.from(map, ([id, val]) => ({ id, name: val.name, name_es: val.name_es }));
  }, [employees]);

  // KPIs — TM-Analytics (approved): headline KPIs come EXCLUSIVELY from
  // released TalentMap results (suite_scores.talentmap). Unevaluated
  // collaborators are excluded from every average/index — never counted as 0.
  // Blended multi-suite value score remains ONLY on the 9-box vertical axis.
  const kpis = useMemo(() => {
    if (!overview) return null;
    const evaluated = employees.filter((e) => e.suite_scores?.talentmap != null);
    const n = evaluated.length;
    const avgTm = n ? Math.round(evaluated.reduce((s, e) => s + e.suite_scores.talentmap, 0) / n) : null;
    const strong = evaluated.filter((e) => e.suite_scores.talentmap >= 70).length;
    const highPotential = evaluated.filter((e) => e.suite_scores.talentmap >= 85).length;
    const atRisk = evaluated.filter((e) => e.suite_scores.talentmap < 50).length;
    const withRoleFit = evaluated.filter((e) => e.role_fit_score != null);
    // Gap index = red role-gaps / compared pairs, ONLY across users with
    // matched requirements + released scores. No config → unavailable (null),
    // never a fabricated 100%.
    let gapPairs = 0; let redPairs = 0;
    for (const u of gap?.users || []) {
      if (!u.comparison_available) continue;
      for (const c of u.competencies || []) {
        if (c.required_level == null || c.actual_level == null) continue;
        gapPairs += 1;
        if (c.color === 'red') redPairs += 1;
      }
    }
    return {
      total: employees.length,
      evaluated: n,
      avg_tm: avgTm,
      strength_pct: n ? Math.round(100 * strong / n) : null,
      gap_pct: gapPairs ? Math.round(100 * redPairs / gapPairs) : null,
      high_potential: n ? highPotential : null,
      at_risk: n ? atRisk : null,
      role_fit_avg: withRoleFit.length
        ? Math.round(withRoleFit.reduce((s, e) => s + e.role_fit_score, 0) / withRoleFit.length)
        : null,
    };
  }, [overview, employees, gap]);

  // Radar — competency aggregation
  const radarData = useMemo(() => {
    if (!gap?.users) return [];
    const byCode = new Map();
    const filtered = deptFilter === '__all__'
      ? gap.users
      : gap.users.filter((u) => {
          const eu = employees.find((e) => e.user?.id === u.user?.id);
          return eu?.department?.id === deptFilter;
        });
    for (const u of filtered) {
      for (const c of u.competencies || []) {
        if (c.color === 'no_data' || c.actual_level == null) continue;
        const row = byCode.get(c.competency_code) || {
          code: c.competency_code,
          label: c.competency_label,
          required_sum: 0,
          required_n: 0,
          actual_sum: 0,
          n: 0,
        };
        if (c.required_level != null) {
          row.required_sum += c.required_level;
          row.required_n += 1;
        }
        row.actual_sum += c.actual_level || 0;
        row.n += 1;
        byCode.set(c.competency_code, row);
      }
    }
    return Array.from(byCode.values()).map((r) => ({
      code: r.code,
      label: r.label,
      // TM-Analytics — never imply a role gap where no required level exists.
      required: r.required_n ? +(r.required_sum / r.required_n).toFixed(2) : null,
      actual: +(r.actual_sum / r.n).toFixed(2),
      gap: r.required_n ? +((r.required_sum / r.required_n) - (r.actual_sum / r.n)).toFixed(2) : null,
      n: r.n,
    }));
  }, [gap, deptFilter, employees]);

  // TM-Analytics — honest radar state (approved states matrix).
  const radarState = useMemo(() => {
    const users = gap?.users || [];
    const evaluated = users.filter((u) => u.state && u.state !== 'no_evaluation');
    if (!evaluated.length) return 'no_evaluations';
    if (evaluated.some((u) => u.state === 'complete')) return 'complete';
    if (evaluated.some((u) => u.state === 'no_requirements')) return 'no_requirements';
    return 'position_not_matched';
  }, [gap]);
  const hasRequired = radarData.some((r) => r.required != null);

  // Critical gaps — gap-analysis where color='red'
  const criticalGaps = useMemo(() => {
    if (!gap?.users) return [];
    const rows = [];
    for (const u of gap.users) {
      for (const c of u.competencies || []) {
        if (c.color === 'red' && c.actual_level != null) {
          rows.push({
            user_id: u.user?.id,
            user_name: u.user?.name,
            position: u.matched_position?.position_title,
            competency_code: c.competency_code,
            competency_label: c.competency_label,
            required: c.required_level,
            actual: c.actual_level,
            gap: c.gap,
          });
        }
      }
    }
    return rows.sort((a, b) => b.gap - a.gap).slice(0, 20);
  }, [gap]);

  // Talent risks — TM-Analytics: evaluated collaborators with LOW released
  // TalentMap score only. Never inferred from missing suite data.
  const talentRisks = useMemo(() => {
    return employees
      .filter((e) => e.suite_scores?.talentmap != null && e.suite_scores.talentmap < 60)
      .sort((a, b) => (a.suite_scores?.talentmap || 0) - (b.suite_scores?.talentmap || 0))
      .slice(0, 12);
  }, [employees]);

  // Growth opportunities — employees with high talentmap suite score
  const growthOpps = useMemo(() => {
    return employees
      .filter((e) => (e.suite_scores?.talentmap ?? 0) >= 65)
      .sort((a, b) => (b.suite_scores?.talentmap || 0) - (a.suite_scores?.talentmap || 0))
      .slice(0, 8);
  }, [employees]);

  // Best / worst departments
  const deptRanked = useMemo(() => {
    return (overview?.department_rollup || [])
      .slice()
      .sort((a, b) => (b.avg_value || 0) - (a.avg_value || 0));
  }, [overview]);

  // Founder UX Review — TalentMap-only department comparison + heat map.
  const tmDeptData = useMemo(() => {
    const map = new Map();
    for (const e of employees) {
      const s = e.suite_scores?.talentmap;
      if (s == null) continue;
      const name = tData(e.department) || '—';
      const row = map.get(name) || { name, sum: 0, n: 0 };
      row.sum += s; row.n += 1;
      map.set(name, row);
    }
    return Array.from(map.values())
      .map((r) => ({ name: r.name, avg: Math.round(r.sum / r.n), n: r.n }))
      .sort((a, b) => b.avg - a.avg);
  }, [employees, tData]);

  const heatData = useMemo(() => {
    if (!gap?.users) return { codes: [], labels: {}, rows: [] };
    const labels = {};
    const byDept = new Map();
    for (const u of gap.users) {
      const eu = employees.find((e) => e.user?.id === u.user?.id);
      const dname = tData(eu?.department) || '—';
      for (const c of u.competencies || []) {
        if (c.color === 'no_data' || c.actual_level == null) continue;
        labels[c.competency_code] = c.competency_label;
        const d = byDept.get(dname) || new Map();
        const cell = d.get(c.competency_code) || { sum: 0, n: 0 };
        cell.sum += c.actual_level; cell.n += 1;
        d.set(c.competency_code, cell);
        byDept.set(dname, d);
      }
    }
    const codes = Object.keys(labels).slice(0, 8);
    const rows = Array.from(byDept.entries()).sort(([a], [b]) => a.localeCompare(b)).map(([dept, cells]) => ({
      dept,
      cells: codes.map((code) => {
        const c = cells.get(code);
        return c ? +(c.sum / c.n).toFixed(1) : null;
      }),
    }));
    return { codes, labels, rows };
  }, [gap, employees, tData]);


  if (loading) {
    return (
      <>
        <Topbar title={t('ctm.title', { defaultValue: 'TalentMap' })}
                subtitle={t('ctm.subtitle', { defaultValue: 'Workforce mapping & talent-gap analysis' })} />
        <div className="py-16 flex items-center justify-center text-muted-foreground" data-testid="ctm-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> {t('common.loading', { defaultValue: 'Loading…' })}
        </div>
      </>
    );
  }

  // Empty state — no employees at all
  if (!overview || employees.length === 0) {
    return (
      <>
        <Topbar title={t('ctm.title', { defaultValue: 'TalentMap' })}
                subtitle={t('ctm.subtitle', { defaultValue: 'Workforce mapping & talent-gap analysis' })} />
        <div className="p-6 lg:p-8" data-testid="client-talentmap">
          <Card className="p-12 text-center shadow-card" data-testid="ctm-empty">
            <MapIcon className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
            <h3 className="font-display text-xl font-bold mb-2">
              {t('ctm.empty.title', { defaultValue: 'No workforce data yet' })}
            </h3>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {t('ctm.empty.body', { defaultValue: 'Add collaborators and run evaluations to populate TalentMap.' })}
            </p>
          </Card>
        </div>
      </>
    );
  }

  const openSheet = (kind, payload) => setSheetState({ open: true, kind, payload });
  const closeSheet = () => setSheetState((s) => ({ ...s, open: false }));

  // Founder UX Review — dedicated TalentMap executive report (was TalentIntelligence).
  const downloadExecutiveReport = async () => {
    try {
      const qs = deptFilter === '__all__' ? '' : `?department_id=${deptFilter}`;
      const res = await api.get(`/client/talentmap/export.pdf${qs}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'TalentMap_Executive_Report.pdf';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <>
      <Topbar
        title={(
          <span className="inline-flex items-center gap-2">
            {t('ctm.title', { defaultValue: 'TalentMap' })}
            <Badge className="bg-primary/15 text-primary border-primary/30">{t('suiteChip.talentmap')}</Badge>
          </span>
        )}
        subtitle={t('ctm.subtitle', { defaultValue: 'Workforce mapping & talent-gap analysis' })}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="gap-1.5" onClick={downloadExecutiveReport} data-testid="ctm-export-pdf">
              <Download className="h-4 w-4" /> {t('ctm.exportReport', { defaultValue: 'Informe Ejecutivo (PDF)' })}
            </Button>
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setRequestOpen(true)} data-testid="ctm-request-evaluation-btn">
              {t('ctm.requestSupport', { defaultValue: 'Solicitar apoyo de CTH' })}
            </Button>
            <Button size="sm" className="gap-1.5" onClick={() => setLaunchOpen(true)} data-testid="ctm-launch-evaluation-btn">
              <Plus className="h-4 w-4" /> {t('ctm.launchEvaluation', { defaultValue: 'Lanzar Evaluación TalentMap' })}
            </Button>
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="client-talentmap">
        {/* Department filter */}
        <div className="flex items-center gap-3" data-testid="ctm-filter-bar">
          <span className="text-sm text-muted-foreground">{t('ctm.filter.department', { defaultValue: 'Department' })}</span>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="w-64" data-testid="ctm-dept-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t('ctm.filter.all', { defaultValue: 'All departments' })}</SelectItem>
              {departments.map((d) => (
                <SelectItem key={d.id} value={d.id}>{tData(d)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground" data-testid="ctm-filter-scope-hint">
            {t('ctm.filter.scopeHint', { defaultValue: 'Applies to the radar view' })}
          </span>
        </div>

        {/* Founder UX Review — onboarding hero when no evaluations exist yet */}
        {kpis.evaluated === 0 && (
          <Card className="p-8 shadow-card border-primary/20 bg-gradient-to-br from-primary/5 to-transparent" data-testid="ctm-empty-hero">
            <div className="max-w-3xl">
              <div className="flex items-center gap-2 mb-2">
                <MapIcon className="h-5 w-5 text-primary" />
                <h2 className="text-lg font-semibold">{t('ctm.hero.title', { defaultValue: 'Bienvenido a TalentMap' })}</h2>
              </div>
              <p className="text-sm text-muted-foreground mb-5">
                {t('ctm.hero.body', { defaultValue: 'TalentMap mapea el talento de tu organización: mide competencias, identifica fortalezas y brechas frente a cada puesto, y revela alto potencial y riesgos para tomar decisiones de desarrollo y sucesión.' })}
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mb-6">
                {[
                  { n: 1, title: t('ctm.hero.step1', { defaultValue: 'Lanza la evaluación' }), body: t('ctm.hero.step1Body', { defaultValue: 'Selecciona la evaluación publicada y a los colaboradores, y asígnala directamente — sin aprobación de CTH.' }) },
                  { n: 2, title: t('ctm.hero.step2', { defaultValue: 'Tus colaboradores responden' }), body: t('ctm.hero.step2Body', { defaultValue: 'Cada colaborador completa su evaluación desde su portal. Puedes solicitar apoyo de CTH en cualquier momento.' }) },
                  { n: 3, title: t('ctm.hero.step3', { defaultValue: 'Recibe analítica ejecutiva' }), body: t('ctm.hero.step3Body', { defaultValue: 'Este panel y el Informe Ejecutivo se poblarán automáticamente con los resultados.' }) },
                ].map((s) => (
                  <div key={s.n} className="rounded-lg border border-border bg-card p-4">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold mb-2">{s.n}</span>
                    <p className="text-sm font-semibold">{s.title}</p>
                    <p className="text-xs text-muted-foreground mt-1">{s.body}</p>
                  </div>
                ))}
              </div>
              <Button onClick={() => setLaunchOpen(true)} className="gap-1.5" data-testid="ctm-hero-request-btn">
                <Plus className="h-4 w-4" /> {t('ctm.hero.cta', { defaultValue: 'Lanzar mi primera Evaluación TalentMap' })}
              </Button>
            </div>
          </Card>
        )}

        {/* Executive KPIs */}
        <AssignmentsTraceCard suite="talentmap" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ctm-kpis">
          <KpiCard icon={Users} tone="primary" testid="ctm-kpi-total"
                   label={t('ctm.kpi.total', { defaultValue: 'Workforce' })}
                   value={kpis.total}
                   hint={t('ctm.kpi.coverage', { defaultValue: '{{n}} of {{total}} evaluated', n: kpis.evaluated, total: kpis.total })} />
          <KpiCard icon={Gauge} tone="info" testid="ctm-kpi-talent"
                   label={t('ctm.kpi.talentScore', { defaultValue: 'Avg. TalentMap score' })}
                   value={kpis.avg_tm ?? '—'}
                   hint={kpis.avg_tm == null
                     ? t('ctm.kpi.noReleased', { defaultValue: 'No released evaluations yet' })
                     : t('ctm.kpi.evaluatedOnly', { defaultValue: 'Evaluated collaborators only' })} />
          <KpiCard icon={TrendingUp} tone="success" testid="ctm-kpi-strength"
                   label={t('ctm.kpi.strength', { defaultValue: 'Strength index' })}
                   value={kpis.strength_pct == null ? '—' : `${kpis.strength_pct}%`}
                   hint={kpis.strength_pct == null
                     ? t('ctm.kpi.noReleased', { defaultValue: 'No released evaluations yet' })
                     : t('ctm.kpi.strengthHint', { defaultValue: 'TalentMap ≥ 70 · evaluated only' })} />
          <KpiCard icon={AlertTriangle} tone="destructive" testid="ctm-kpi-gap"
                   label={t('ctm.kpi.gap', { defaultValue: 'Gap index' })}
                   value={kpis.gap_pct == null ? '—' : `${kpis.gap_pct}%`}
                   hint={kpis.gap_pct == null
                     ? t('ctm.kpi.gapUnavailable', { defaultValue: 'Requires configured position requirements' })
                     : t('ctm.kpi.gapHint', { defaultValue: 'Critical role-gaps / compared pairs' })} />
          <KpiCard icon={Crown} tone="success" testid="ctm-kpi-stars"
                   label={t('ctm.kpi.stars', { defaultValue: 'High potential' })}
                   value={kpis.high_potential ?? '—'}
                   hint={kpis.high_potential == null
                     ? t('ctm.kpi.noReleased', { defaultValue: 'No released evaluations yet' })
                     : t('ctm.kpi.starsHint', { defaultValue: 'TalentMap ≥ 85' })} />
          <KpiCard icon={ShieldAlert} tone="warning" testid="ctm-kpi-at-risk"
                   label={t('ctm.kpi.atRisk', { defaultValue: 'At risk' })}
                   value={kpis.at_risk ?? '—'}
                   hint={kpis.at_risk == null
                     ? t('ctm.kpi.noReleased', { defaultValue: 'No released evaluations yet' })
                     : t('ctm.kpi.atRiskHint', { defaultValue: 'TalentMap < 50 · evaluated only' })} />
          <KpiCard icon={Target} tone="info" testid="ctm-kpi-rolefit"
                   label={t('ctm.kpi.roleFit', { defaultValue: 'Avg. role fit' })}
                   value={kpis.role_fit_avg ?? '—'}
                   hint={kpis.role_fit_avg == null
                     ? t('ctm.kpi.noRoleProfile', { defaultValue: 'No role profile' })
                     : null} />
          <KpiCard icon={RadarIcon} tone="primary" testid="ctm-kpi-radar-n"
                   label={t('ctm.kpi.radarN', { defaultValue: 'Competencies tracked' })}
                   value={radarData.length}
                   hint={radarData.length ? null : t(`ctm.radar.state.${radarState}`, {
                     defaultValue: 'No released TalentMap evaluations yet',
                   })} />
        </div>

        {/* Founder UAT 2026-06 — organizational analytics (real data, no line charts) */}
        {kpis.total > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="ctm-analytics-row">
            <DonutCard
              title={t('ctm.analytics.coverageTitle', { defaultValue: 'Cobertura de evaluación' })}
              subtitle={t('ctm.analytics.coverageSubtitle', { defaultValue: 'Colaboradores evaluados vs. pendientes' })}
              testid="ctm-chart-coverage"
              accent={SEM.blue}
              data={[
                { name: t('ctm.analytics.evaluated', { defaultValue: 'Evaluados' }), value: kpis.evaluated || 0, color: SEM.green },
                { name: t('ctm.analytics.pending', { defaultValue: 'Pendientes' }), value: Math.max(0, (kpis.total || 0) - (kpis.evaluated || 0)), color: SEM.yellow },
              ]}
              emptyText={t('ctm.analytics.empty', { defaultValue: 'No hay suficientes evaluaciones completadas para generar esta visualización.' })}
            />
            <BarsCard
              title={t('ctm.analytics.quadrantTitle', { defaultValue: 'Distribución por cuadrante (9-box)' })}
              subtitle={t('ctm.analytics.quadrantSubtitle', { defaultValue: 'Solo evaluaciones liberadas' })}
              testid="ctm-chart-quadrants"
              accent={SEM.violet}
              data={Object.entries(overview?.quadrant_counts || {})
                .filter(([, v]) => v > 0)
                .map(([k, v], i) => ({
                  name: t(`ctm.quadrant.${k}`, { defaultValue: k.replace(/_/g, ' ') }),
                  value: v,
                  color: [SEM.green, SEM.blue, SEM.teal, SEM.violet, SEM.orange, SEM.yellow, SEM.red, '#EC4899', SEM.slate][i % 9],
                }))}
              emptyText={t('ctm.analytics.empty', { defaultValue: 'No hay suficientes evaluaciones completadas para generar esta visualización.' })}
            />
          </div>
        )}

        <Tabs defaultValue="radar">
          <TabsList data-testid="ctm-tabs">
            <TabsTrigger value="radar" data-testid="ctm-tab-radar">{t('ctm.tabs.radar', { defaultValue: 'Radar' })}</TabsTrigger>
            <TabsTrigger value="matrix" data-testid="ctm-tab-matrix">{t('ctm.tabs.matrix', { defaultValue: '9-box matrix' })}</TabsTrigger>
            <TabsTrigger value="teams" data-testid="ctm-tab-teams">{t('ctm.tabs.teams', { defaultValue: 'Teams' })}</TabsTrigger>
            <TabsTrigger value="gaps" data-testid="ctm-tab-gaps">{t('ctm.tabs.gaps', { defaultValue: 'Critical gaps' })}</TabsTrigger>
            <TabsTrigger value="risks" data-testid="ctm-tab-risks">{t('ctm.tabs.risks', { defaultValue: 'Risks' })}</TabsTrigger>
            <TabsTrigger value="growth" data-testid="ctm-tab-growth">{t('ctm.tabs.growth', { defaultValue: 'Growth' })}</TabsTrigger>
            {SUCCESSION_TAB_ENABLED && (
              <TabsTrigger value="succession" data-testid="ctm-tab-succession">{t('ctm.tabs.succession', { defaultValue: 'Succession' })}</TabsTrigger>
            )}
          </TabsList>

          {/* RADAR */}
          <TabsContent value="radar" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ctm-radar-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-base">
                  {t('ctm.radar.title', { defaultValue: 'Strength & gap radar' })}
                </h3>
                {radarData.length > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {hasRequired
                      ? t('ctm.radar.legend', { defaultValue: 'Required (target) vs actual — gap = required − actual' })
                      : t('ctm.radar.legendActualOnly', { defaultValue: 'Actual competency averages — role-based comparison unavailable' })}
                  </span>
                )}
              </div>
              {radarData.length > 0 && radarState !== 'complete' && (
                <div className="mb-3 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs" data-testid="ctm-radar-partial-banner">
                  <ShieldAlert className="h-3.5 w-3.5 mt-0.5 text-warning shrink-0" />
                  <span>{t(`ctm.radar.state.${radarState}`, { defaultValue: 'Role-based comparison unavailable.' })}</span>
                </div>
              )}
              {radarData.length === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ctm-radar-empty">
                  <Target className="h-8 w-8 opacity-30 mx-auto mb-2" />
                  {t(`ctm.radar.state.${radarState}`, {
                    defaultValue: 'No released TalentMap evaluations yet for this scope.',
                  })}
                </div>
              ) : (
                <>
                  <div style={{ height: 360 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RadarChart data={radarData}>
                        <PolarGrid />
                        <PolarAngleAxis dataKey="label" tick={{ fontSize: 11 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 5]} tick={{ fontSize: 10 }} />
                        <Radar name={t('ctm.radar.actual', { defaultValue: 'Actual' })}
                               dataKey="actual" stroke="hsl(var(--primary))"
                               fill="hsl(var(--primary))" fillOpacity={0.35} />
                        {hasRequired && (
                          <Radar name={t('ctm.radar.required', { defaultValue: 'Required' })}
                                 dataKey="required" stroke="hsl(var(--muted-foreground))"
                                 fill="hsl(var(--muted-foreground))" fillOpacity={0.15} />
                        )}
                        <Tooltip />
                        <Legend />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>
                  <Table className="mt-4" data-testid="ctm-radar-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>{t('ctm.radar.competency', { defaultValue: 'Competency' })}</TableHead>
                        <TableHead className="text-right">{t('ctm.radar.required', { defaultValue: 'Required' })}</TableHead>
                        <TableHead className="text-right">{t('ctm.radar.actual', { defaultValue: 'Actual' })}</TableHead>
                        <TableHead className="text-right">{t('ctm.radar.gap', { defaultValue: 'Gap' })}</TableHead>
                        <TableHead className="text-right">N</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {radarData.map((r) => (
                        <TableRow key={r.code} className="cursor-pointer hover:bg-muted/40"
                                  onClick={() => openSheet('competency', { competency: r, deptFilter, gap })}
                                  data-testid={`ctm-radar-row-${r.code}`}>
                          <TableCell className="font-medium">{r.label}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.required ?? '—'}</TableCell>
                          <TableCell className="text-right tabular-nums">{r.actual}</TableCell>
                          <TableCell className={`text-right tabular-nums font-bold ${r.gap == null ? 'text-muted-foreground' : r.gap > 0.5 ? 'text-destructive' : r.gap > 0 ? 'text-warning' : 'text-success'}`}>
                            {r.gap == null ? '—' : r.gap > 0 ? `+${r.gap}` : r.gap}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-muted-foreground">{r.n}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              )}
            </Card>
          </TabsContent>

          {/* 9-BOX */}
          <TabsContent value="matrix" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ctm-matrix-card">
              <h3 className="font-semibold text-base mb-3">
                {t('ctm.matrix.title', { defaultValue: '9-box performance × potential matrix' })}
              </h3>
              {/* 9-box integrity (Founder UAT 2026-06) — unevaluated collaborators
                  are NEVER classified into a quadrant. Truthful empty state. */}
              {kpis.evaluated === 0 ? (
                <div className="py-10 text-center text-sm text-muted-foreground" data-testid="ctm-matrix-empty">
                  <Target className="h-8 w-8 opacity-30 mx-auto mb-2" />
                  {t('ctm.matrix.empty', { defaultValue: 'Aún no hay evaluaciones TalentMap liberadas. La matriz 9-box se poblará cuando CTH libere los primeros resultados — los colaboradores sin evaluación no se clasifican en ningún cuadrante.' })}
                </div>
              ) : (
              <>
              <div className="grid grid-cols-3 gap-2" data-testid="ctm-matrix-grid">
                {QUADRANT_KEYS.flatMap((row) =>
                  row.map((q) => {
                    const count = overview.quadrant_counts?.[q] || 0;
                    return (
                      <button
                        key={q}
                        className={`rounded-lg border ${QUADRANT_TONE[q]} p-4 text-left transition-smooth hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary`}
                        onClick={() => openSheet('quadrant', { quadrant: q, employees: employees.filter((e) => e.quadrant === q) })}
                        data-testid={`ctm-cell-${q}`}
                      >
                        <p className="text-xs uppercase font-semibold tracking-wider">
                          {t(`ctm.matrix.${q}`, { defaultValue: q.replace('_', ' ') })}
                        </p>
                        <p className="text-2xl font-bold tabular-nums">{count}</p>
                      </button>
                    );
                  }),
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {t('ctm.matrix.axes', { defaultValue: 'Vertical = value score · Horizontal = potential (talentmap suite score)' })}
              </p>
              </>
              )}
            </Card>
          </TabsContent>

          {/* TEAMS */}
          <TabsContent value="teams" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ctm-teams-card">
              <h3 className="font-semibold text-base mb-3">
                {t('ctm.teams.title', { defaultValue: 'Department performance' })}
              </h3>
              {!hasTalentMapData ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="ctm-teams-needs-releases">
                  <Users className="h-6 w-6 opacity-50 mx-auto mb-2" />
                  {t('ctm.teams.needsReleases', { defaultValue: 'Department statistics will appear once the first TalentMap evaluations are released for your organization.' })}
                </p>
              ) : deptRanked.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="ctm-teams-empty">
                  <Users className="h-6 w-6 opacity-50 mx-auto mb-2" />
                  {t('ctm.teams.empty', { defaultValue: 'No department roll-up yet.' })}
                </p>
              ) : (
                <Table data-testid="ctm-teams-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ctm.teams.dept', { defaultValue: 'Department' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.teams.employees', { defaultValue: 'Employees' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.teams.avg', { defaultValue: 'Avg. score' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.teams.stars', { defaultValue: 'Stars' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.teams.atRisk', { defaultValue: 'At risk' })}</TableHead>
                      <TableHead>{t('ctm.teams.bar', { defaultValue: 'Visual' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deptRanked.map((d) => (
                      <TableRow key={d.department?.id || d.department?.name}
                                className="cursor-pointer hover:bg-muted/40"
                                onClick={() => openSheet('dept', { department: d })}
                                data-testid={`ctm-team-row-${d.department?.id || d.department?.name}`}>
                        <TableCell className="font-medium">{tData(d.department)}</TableCell>
                        <TableCell className="text-right tabular-nums">{d.employee_count}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold">{Math.round(d.avg_value || 0)}</TableCell>
                        <TableCell className="text-right tabular-nums text-success">{d.stars}</TableCell>
                        <TableCell className="text-right tabular-nums text-destructive">{d.at_risk}</TableCell>
                        <TableCell className="w-32">
                          <Progress value={Math.min(100, d.avg_value || 0)} className="h-1.5" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>

            {/* Founder UX Review — TalentMap department comparison chart */}
            {tmDeptData.length > 0 && (
              <Card className="p-5 shadow-card mt-4" data-testid="ctm-dept-chart-card">
                <h3 className="font-semibold text-base mb-3">
                  {t('ctm.teams.chartTitle', { defaultValue: 'Comparación TalentMap por departamento' })}
                </h3>
                <div style={{ height: Math.max(180, tmDeptData.length * 44) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tmDeptData} layout="vertical" margin={{ left: 16, right: 24 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`${v}/100`, t('ctm.teams.avgTm', { defaultValue: 'TalentMap prom.' })]} />
                      <Bar dataKey="avg" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} barSize={18} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            )}

            {/* Founder UX Review — dept × competency heat map */}
            {heatData.rows.length > 0 && heatData.codes.length > 0 && (
              <Card className="p-5 shadow-card mt-4 overflow-x-auto" data-testid="ctm-heatmap-card">
                <h3 className="font-semibold text-base mb-1">
                  {t('ctm.heat.title', { defaultValue: 'Mapa de calor de competencias' })}
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  {t('ctm.heat.hint', { defaultValue: 'Nivel actual promedio (1–5) por departamento y competencia.' })}
                </p>
                <table className="w-full text-xs" data-testid="ctm-heatmap-table">
                  <thead>
                    <tr>
                      <th className="text-left p-2 font-semibold">{t('ctm.teams.dept', { defaultValue: 'Departamento' })}</th>
                      {heatData.codes.map((c) => (
                        <th key={c} className="p-2 font-semibold text-center">{heatData.labels[c]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heatData.rows.map((r) => (
                      <tr key={r.dept}>
                        <td className="p-2 font-medium whitespace-nowrap">{r.dept}</td>
                        {r.cells.map((v, i) => (
                          <td key={i} className={`p-2 text-center tabular-nums font-semibold rounded-sm ${
                            v == null ? 'text-muted-foreground/50'
                              : v < 1.5 ? 'bg-red-200 text-red-900'
                              : v < 2.5 ? 'bg-orange-200 text-orange-900'
                              : v < 3.25 ? 'bg-amber-100 text-amber-900'
                              : v < 4 ? 'bg-green-100 text-green-900'
                              : 'bg-green-200 text-green-900'}`}>
                            {v == null ? '—' : v}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </TabsContent>

          {/* CRITICAL GAPS */}
          <TabsContent value="gaps" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ctm-gaps-card">
              <h3 className="font-semibold text-base mb-3 inline-flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                {t('ctm.gaps.title', { defaultValue: 'Critical competency gaps' })}
              </h3>
              {criticalGaps.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="ctm-gaps-empty">
                  <Sparkles className="h-6 w-6 opacity-50 mx-auto mb-2" />
                  {t('ctm.gaps.empty', { defaultValue: 'No critical gaps. Either no competency evaluations have been run yet, or every collaborator is meeting their position\'s threshold.' })}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ctm.gaps.user', { defaultValue: 'Collaborator' })}</TableHead>
                      <TableHead>{t('ctm.gaps.position', { defaultValue: 'Position' })}</TableHead>
                      <TableHead>{t('ctm.gaps.competency', { defaultValue: 'Competency' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.gaps.required', { defaultValue: 'Required' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.gaps.actual', { defaultValue: 'Actual' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.gaps.gap', { defaultValue: 'Gap' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {criticalGaps.map((g, i) => (
                      <TableRow key={`${g.user_id}-${g.competency_code}`}
                                className="cursor-pointer hover:bg-muted/40"
                                onClick={() => openSheet('collab', { employee: employees.find((e) => e.user?.id === g.user_id) })}
                                data-testid={`ctm-gap-row-${i}`}>
                        <TableCell className="font-medium">{g.user_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{g.position || '—'}</TableCell>
                        <TableCell>{g.competency_label}</TableCell>
                        <TableCell className="text-right tabular-nums">{g.required}</TableCell>
                        <TableCell className="text-right tabular-nums">{g.actual}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-destructive">+{g.gap?.toFixed(2)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          {/* RISKS */}
          <TabsContent value="risks" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ctm-risks-card">
              <h3 className="font-semibold text-base mb-3 inline-flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-destructive" />
                {t('ctm.risks.title', { defaultValue: 'Talent risks' })}
                <span
                  className="cursor-help text-muted-foreground"
                  title={t('ctm.risks.hint', { defaultValue: 'Risk classification is based on the TalentMap score (below 60). The column shows the blended value score.' })}
                  data-testid="ctm-risks-info"
                >
                  <Info className="h-3.5 w-3.5" />
                </span>
              </h3>
              {talentRisks.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="ctm-risks-empty">
                  <Sparkles className="h-6 w-6 opacity-50 mx-auto mb-2" />
                  {t('ctm.risks.empty', { defaultValue: 'No talent risks detected.' })}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ctm.risks.user', { defaultValue: 'Collaborator' })}</TableHead>
                      <TableHead>{t('ctm.risks.dept', { defaultValue: 'Department' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.risks.score', { defaultValue: 'Value score' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.risks.rolefit', { defaultValue: 'Role fit' })}</TableHead>
                      <TableHead className="text-right">{t('common.actions', { defaultValue: 'Actions' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {talentRisks.map((e) => (
                      <TableRow key={e.user?.id} className="cursor-pointer hover:bg-muted/40"
                                onClick={() => openSheet('collab', { employee: e })}
                                data-testid={`ctm-risk-row-${e.user?.id}`}>
                        <TableCell className="font-medium">{e.user?.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{tData(e.department) || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-destructive">{e.value?.value_score}</TableCell>
                        <TableCell className="text-right tabular-nums">{e.role_fit_score ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost"
                                  data-testid={`ctm-risk-open-${e.user?.id}`}>
                            <ArrowRight className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          {/* GROWTH */}
          <TabsContent value="growth" className="mt-4">
            <Card className="p-5 shadow-card" data-testid="ctm-growth-card">
              <h3 className="font-semibold text-base mb-3 inline-flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-success" />
                {t('ctm.growth.title', { defaultValue: 'Growth opportunities' })}
              </h3>
              {growthOpps.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center" data-testid="ctm-growth-empty">
                  <TrendingUp className="h-6 w-6 opacity-50 mx-auto mb-2" />
                  {t('ctm.growth.empty', { defaultValue: 'No growth opportunities detected yet.' })}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('ctm.growth.user', { defaultValue: 'Collaborator' })}</TableHead>
                      <TableHead>{t('ctm.growth.dept', { defaultValue: 'Department' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.growth.score', { defaultValue: 'Value score' })}</TableHead>
                      <TableHead className="text-right">{t('ctm.growth.potential', { defaultValue: 'Potential' })}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {growthOpps.map((e) => (
                      <TableRow key={e.user?.id} className="cursor-pointer hover:bg-muted/40"
                                onClick={() => openSheet('collab', { employee: e })}
                                data-testid={`ctm-growth-row-${e.user?.id}`}>
                        <TableCell className="font-medium">{e.user?.name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{tData(e.department) || '—'}</TableCell>
                        <TableCell className="text-right tabular-nums">{e.value?.value_score}</TableCell>
                        <TableCell className="text-right tabular-nums font-bold text-success">{e.suite_scores?.talentmap}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Card>
          </TabsContent>

          {/* SUCCESSION — honest empty state (hidden per founder freeze decision 2026-08-29; restore only with a real succession engine) */}
          {SUCCESSION_TAB_ENABLED && (
          <TabsContent value="succession" className="mt-4">
            <Card className="p-8 text-center shadow-card" data-testid="ctm-succession-empty">
              <Crown className="h-8 w-8 text-muted-foreground/30 mx-auto mb-3" />
              <h3 className="font-semibold text-base mb-1">
                {t('ctm.succession.title', { defaultValue: 'Succession data not configured yet' })}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {t('ctm.succession.body', {
                  defaultValue: 'Succession data will be available once Position Builder targets are configured. We do not fabricate readiness or ETAs.',
                })}
              </p>
            </Card>
          </TabsContent>
          )}
        </Tabs>
      </div>

      <ClientEvaluationRequestDialog
        open={requestOpen}
        onOpenChange={setRequestOpen}
        defaultSuite="talentmap"
        onCreated={load}
      />

      <ClientSelfServeAssignDialog
        open={launchOpen}
        onOpenChange={setLaunchOpen}
        suite="talentmap"
        onCreated={load}
      />

      <TalentMapDetailSheet
        open={sheetState.open}
        onOpenChange={(o) => (o ? null : closeSheet())}
        kind={sheetState.kind}
        payload={sheetState.payload}
      />
    </>
  );
}
